import type { IAppTranslationRepository } from './i18n-repository.interface';
import type { TranslationResources, Language } from '../types/i18n-types';
import { SUPPORTED_LANGUAGES } from '../types/i18n-types';

/**
 * Local file-based translation repository
 * Loads translations from flat JSON files in public/locales/
 *
 * File structure expected:
 * public/locales/
 * ├── en.json (all namespaces with keys like "common.buttons.save", "assignEv.page.title")
 * └── he.json (all namespaces with keys like "common.buttons.save", "assignEv.page.title")
 */
export class LocalTranslationRepository implements IAppTranslationRepository {
  private readonly basePath = '/locales';
  private cache: Map<string, Record<string, string>> = new Map();

  /**
   * Load translations from local flat JSON file
   * @param locale - Language code (e.g., 'en', 'he')
   * @param namespace - Translation namespace (e.g., 'common', 'assignEv')
   */
  async loadTranslations(locale: string, namespace: string): Promise<TranslationResources> {
    // Load full language file (cached)
    const allTranslations = await this.loadLanguageFile(locale);

    // Filter and transform keys for the requested namespace
    // Flat file has keys like "namespace.key.path", i18next expects "key.path" per namespace
    const namespacePrefix = `${namespace}.`;
    const namespacedTranslations: TranslationResources = {};

    for (const [key, value] of Object.entries(allTranslations)) {
      if (key.startsWith(namespacePrefix)) {
        // Strip the namespace prefix
        const keyWithoutNamespace = key.substring(namespacePrefix.length);
        namespacedTranslations[keyWithoutNamespace] = value;
      }
    }

    if (import.meta.env.DEV) {
      console.log(
        `[i18n] Local: Loaded ${Object.keys(namespacedTranslations).length} keys for ${locale}:${namespace}`
      );
    }

    return namespacedTranslations;
  }

  /**
   * Load full language file from flat JSON
   * @param locale - Language code (e.g., 'en', 'he')
   */
  private async loadLanguageFile(locale: string): Promise<Record<string, string>> {
    // Return cached version if available
    if (this.cache.has(locale)) {
      return this.cache.get(locale)!;
    }

    const url = `${this.basePath}/${locale}.json`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        // If locale-specific file not found, try fallback to English
        if (response.status === 404 && locale !== 'en') {
          console.warn(`[i18n] Translation file not found: ${url}, falling back to English`);
          return this.loadLanguageFile('en');
        }
        throw new Error(`Failed to load translations: ${response.status} ${response.statusText}`);
      }

      const resources = await response.json();
      this.cache.set(locale, resources);

      if (import.meta.env.DEV) {
        console.log(`[i18n] Loaded flat translation file: ${locale}.json (${Object.keys(resources).length} keys)`);
      }

      return resources;
    } catch (error) {
      console.error(`[i18n] Failed to load ${url}:`, error);
      throw error;
    }
  }

  /**
   * Get list of available languages
   */
  async getAvailableLanguages(): Promise<Language[]> {
    return SUPPORTED_LANGUAGES;
  }

  /**
   * Clear the translation cache
   * Useful for development/testing or when translations are updated
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Remove a specific language file from cache
   * @param locale - Language code
   */
  invalidateCache(locale: string): void {
    this.cache.delete(locale);
  }
}
